import { ApiError, describeError, useApi } from '@classgo/app-kit';
import type { PlanPeriod, SeedPreviewTopic, TopicView } from '@classgo/shared';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Select,
  useToast,
} from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { listQuery } from '../../academic/api';
import { ConfirmDialog } from '../../components/confirm';
import { curriculumApi, curriculumQueries } from '../../curriculum/api';
import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * The plan de estudios (ticket #69): the subjects a grade studies, and per subject the topics
 * with their logros in periods as columns, reorderable, seeded from ClassGO's DBA set with a
 * preview; read-only for people without `curriculum:write`.
 */
export const CurriculumScreen = () => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const canWrite = session.permissions.all || session.permissions.keys.includes('curriculum:write');
  const grades = useQuery(listQuery(api, 'grades'));
  const [gradeId, setGradeId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const gradeSubjects = useQuery(curriculumQueries.gradeSubjects(api, gradeId));
  const [editingSubjects, setEditingSubjects] = useState(false);
  return (
    <div className="space-y-4">
      <Card title={t('curriculum.title')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('curriculum.grade')}
            value={gradeId}
            onChange={(event) => {
              setGradeId(event.target.value);
              setSubjectId('');
            }}
            options={[
              { value: '', label: t('assignments.pick') },
              ...(grades.data?.items ?? []).map((grade) => ({
                value: grade.id,
                label: grade.name,
              })),
            ]}
          />
          <Select
            label={t('curriculum.subject')}
            value={subjectId}
            disabled={!gradeId}
            onChange={(event) => setSubjectId(event.target.value)}
            options={[
              { value: '', label: t('assignments.pick') },
              ...(gradeSubjects.data ?? []).map((subject) => ({
                value: subject.subjectId,
                label: `${subject.subjectName} (${t('curriculum.topicsCount', { count: subject.topicsCount })})`,
              })),
            ]}
          />
        </div>
        {gradeId && canWrite ? (
          <div className="mt-3">
            <Button variant="secondary" size="sm" onClick={() => setEditingSubjects(true)}>
              {t('curriculum.subjects.edit')}
            </Button>
          </div>
        ) : null}
        {gradeId && gradeSubjects.isSuccess && gradeSubjects.data.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">{t('curriculum.subjects.empty')}</p>
        ) : null}
      </Card>
      {gradeId && subjectId ? (
        <Plan gradeId={gradeId} subjectId={subjectId} canWrite={canWrite} />
      ) : null}
      <Modal
        open={editingSubjects}
        onClose={() => setEditingSubjects(false)}
        title={t('curriculum.subjects.edit')}
      >
        {editingSubjects ? (
          <GradeSubjectsForm gradeId={gradeId} onDone={() => setEditingSubjects(false)} />
        ) : null}
      </Modal>
    </div>
  );
};

const GradeSubjectsForm = ({ gradeId, onDone }: { gradeId: string; onDone: () => void }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const subjects = useQuery(listQuery(api, 'subjects'));
  const current = useQuery(curriculumQueries.gradeSubjects(api, gradeId));
  const [selected, setSelected] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (current.data ?? []).map((row) => [
        row.subjectId,
        row.weeklyHours === null ? '' : String(row.weeklyHours),
      ]),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      curriculumApi(api).putGradeSubjects(gradeId, {
        subjects: Object.entries(selected).map(([id, hours]) => ({
          subjectId: id,
          ...(hours === '' ? {} : { weeklyHours: Number(hours) }),
        })),
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['curriculum', 'grade-subjects', gradeId] });
      onDone();
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <ul className="max-h-80 space-y-2 overflow-auto">
        {(subjects.data?.items ?? []).map((subject) => {
          const checked = subject.id in selected;
          return (
            <li
              key={subject.id}
              className="flex items-center justify-between gap-3 rounded-control bg-gray-50 px-3 py-2"
            >
              <Checkbox
                label={subject.name}
                checked={checked}
                onChange={(event) =>
                  setSelected((rows) => {
                    const next = { ...rows };
                    if (event.target.checked) next[subject.id] = '';
                    else delete next[subject.id];
                    return next;
                  })
                }
              />
              {checked ? (
                <div className="w-28">
                  <Input
                    label={t('curriculum.weeklyHours')}
                    type="number"
                    min={0}
                    max={40}
                    value={selected[subject.id] ?? ''}
                    onChange={(event) =>
                      setSelected((rows) => ({ ...rows, [subject.id]: event.target.value }))
                    }
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const Plan = ({
  gradeId,
  subjectId,
  canWrite,
}: {
  gradeId: string;
  subjectId: string;
  canWrite: boolean;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const plan = useQuery(curriculumQueries.plan(api, gradeId, subjectId));
  const [editing, setEditing] = useState<{ period: number; topic: TopicView | null } | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [deleting, setDeleting] = useState<TopicView | null>(null);
  const refresh = () => queries.invalidateQueries({ queryKey: ['curriculum'] });
  const reorder = useMutation({
    mutationFn: (input: { period: number; topicIds: string[] }) =>
      curriculumApi(api).reorder({
        gradeId,
        subjectId,
        periodPosition: input.period,
        topicIds: input.topicIds,
      }),
    onSuccess: () => void refresh(),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('curriculum.reorder'),
        description: describeError(t, error),
      }),
  });
  const move = (period: number, topics: TopicView[], index: number, delta: number) => {
    const ids = topics.map((topic) => topic.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target] as string, ids[index] as string];
    reorder.mutate({ period, topicIds: ids });
  };
  if (plan.isPending) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (plan.isError)
    return (
      <EmptyState title={t('entity.error.title')} description={describeError(t, plan.error)} />
    );
  const total = plan.data.periods.reduce((sum, period) => sum + period.topics.length, 0);
  const periods = plan.data.yearPeriods;
  const lastPeriod = periods[periods.length - 1]?.position ?? 1;
  // Topics whose period is beyond the year's calendar (a year that lost a period) show in the last column.
  const topicsOf = (position: number) =>
    plan.data.periods
      .filter((row) =>
        position === lastPeriod ? row.periodPosition >= position : row.periodPosition === position,
      )
      .flatMap((row) => row.topics);
  return (
    <Card
      title={t('curriculum.plan')}
      actions={
        canWrite ? (
          <div className="flex gap-2">
            {total === 0 ? (
              <Button onClick={() => setSeeding(true)}>{t('curriculum.seed')}</Button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {total === 0 ? (
        <p className="mb-3 text-sm text-gray-500">{t('curriculum.plan.empty')}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {periods.map(({ position: period, name }) => {
          const topics = topicsOf(period);
          return (
            <section
              key={period}
              aria-label={name}
              className="rounded-card border border-gray-200 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">{name}</h3>
                {canWrite ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing({ period, topic: null })}
                  >
                    {t('curriculum.topic.add')}
                  </Button>
                ) : null}
              </div>
              <ol className="space-y-2">
                {topics.map((topic, index) => (
                  <li key={topic.id} className="rounded-control bg-gray-50 p-2 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{topic.title}</p>
                        {topic.logros.length > 0 ? (
                          <ul className="mt-1 list-disc pl-4 text-xs text-gray-600">
                            {topic.logros.map((logro) => (
                              <li key={logro}>{logro}</li>
                            ))}
                          </ul>
                        ) : null}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <Badge tone={topic.source === 'dba' ? 'info' : 'gray'}>
                            {t(`curriculum.source.${topic.source}`)}
                          </Badge>
                          {topic.dbaReference ? (
                            <span className="text-xs text-gray-500">{topic.dbaReference}</span>
                          ) : null}
                        </div>
                      </div>
                      {canWrite ? (
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            aria-label={t('curriculum.up')}
                            className="control-touch flex min-w-11 items-center justify-center rounded-control text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                            disabled={index === 0}
                            onClick={() => move(period, topics, index, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            aria-label={t('curriculum.down')}
                            className="control-touch flex min-w-11 items-center justify-center rounded-control text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                            disabled={index === topics.length - 1}
                            onClick={() => move(period, topics, index, 1)}
                          >
                            ▼
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {canWrite ? (
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          className="control-touch inline-flex items-center rounded-control px-1 text-xs text-brand-600 underline underline-offset-2 hover:decoration-2"
                          onClick={() => setEditing({ period, topic })}
                        >
                          {t('curriculum.topic.edit')}
                        </button>
                        <button
                          type="button"
                          className="control-touch inline-flex items-center rounded-control px-1 text-xs text-danger-700 underline underline-offset-2 hover:decoration-2"
                          onClick={() => setDeleting(topic)}
                        >
                          {t('curriculum.topic.delete')}
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
      </div>
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.topic ? t('curriculum.topic.edit') : t('curriculum.topic.add')}
      >
        {editing ? (
          <TopicForm
            gradeId={gradeId}
            subjectId={subjectId}
            periods={periods}
            period={editing.period}
            topic={editing.topic}
            onDone={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={seeding}
        onClose={() => setSeeding(false)}
        title={t('curriculum.seed')}
        size="lg"
      >
        {seeding ? (
          <SeedPreview
            gradeId={gradeId}
            subjectId={subjectId}
            periods={periods}
            onDone={() => {
              setSeeding(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        title={t('curriculum.topic.delete')}
        body={t('curriculum.topic.delete.body', { title: deleting?.title ?? '' })}
        confirmLabel={t('curriculum.topic.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void curriculumApi(api)
            .deleteTopic(deleting.id)
            .then(() => {
              setDeleting(null);
              void refresh();
            });
        }}
      />
    </Card>
  );
};

const TopicForm = ({
  gradeId,
  subjectId,
  period,
  periods,
  topic,
  onDone,
}: {
  gradeId: string;
  subjectId: string;
  period: number;
  periods: PlanPeriod[];
  topic: TopicView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const [title, setTitle] = useState(topic?.title ?? '');
  const [logros, setLogros] = useState((topic?.logros ?? []).join('\n'));
  const [dbaReference, setDbaReference] = useState(topic?.dbaReference ?? '');
  const [periodPosition, setPeriodPosition] = useState(String(topic?.periodPosition ?? period));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const list = logros
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      return topic
        ? curriculumApi(api).updateTopic(topic.id, {
            title,
            logros: list,
            dbaReference: dbaReference.trim() === '' ? null : dbaReference.trim(),
            periodPosition: Number(periodPosition),
          })
        : curriculumApi(api).createTopic({
            gradeId,
            subjectId,
            periodPosition: Number(periodPosition),
            title,
            logros: list,
            ...(dbaReference.trim() ? { dbaReference: dbaReference.trim() } : {}),
          });
    },
    onSuccess: onDone,
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Input
        label={t('curriculum.topic.title')}
        required
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Select
        label={t('curriculum.topic.period')}
        value={periodPosition}
        onChange={(event) => setPeriodPosition(event.target.value)}
        options={periods.map((p) => ({ value: String(p.position), label: p.name }))}
      />
      <div>
        <label className="block text-sm font-medium text-gray-900" htmlFor="topic-logros">
          {t('curriculum.topic.logros')}
        </label>
        <textarea
          id="topic-logros"
          className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
          rows={4}
          value={logros}
          onChange={(event) => setLogros(event.target.value)}
        />
        <p className="mt-1 text-xs text-gray-500">{t('curriculum.topic.logros.help')}</p>
      </div>
      <Input
        label={t('curriculum.topic.dba')}
        value={dbaReference}
        onChange={(event) => setDbaReference(event.target.value)}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

/**
 * The seed's refusals in the coordinator's words: an unrecognized grade, or an area the seed does
 * not cover; anything else keeps the generic description.
 */
const seedErrorText = (t: ReturnType<typeof useT>, thrown: unknown): string => {
  if (thrown instanceof ApiError && thrown.code === 'VALIDATION_FAILED') {
    return thrown.issues.some((issue) => issue.path === 'grade')
      ? t('curriculum.seed.grade')
      : t('curriculum.seed.none');
  }
  return describeError(t, thrown);
};

const SeedPreview = ({
  gradeId,
  subjectId,
  periods,
  onDone,
}: {
  gradeId: string;
  subjectId: string;
  periods: PlanPeriod[];
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const preview = useQuery(curriculumQueries.seedPreview(api, gradeId, subjectId, true));
  const items: SeedPreviewTopic[] = Array.isArray(preview.data)
    ? preview.data
    : (preview.data?.items ?? []);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const seed = useMutation({
    mutationFn: () =>
      curriculumApi(api).seed({
        gradeId,
        subjectId,
        ...(excluded.size > 0
          ? { keys: items.filter((item) => !excluded.has(item.key)).map((item) => item.key) }
          : {}),
      }),
    onSuccess: onDone,
    onError: (thrown) => setError(seedErrorText(t, thrown)),
  });
  if (preview.isPending) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (preview.isError)
    return <p className="text-sm text-danger-700">{seedErrorText(t, preview.error)}</p>;
  if (items.length === 0)
    return <p className="text-sm text-gray-500">{t('curriculum.seed.none')}</p>;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{t('curriculum.seed.help')}</p>
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <ul className="max-h-96 space-y-2 overflow-auto">
        {items.map((item) => (
          <li key={item.key} className="rounded-control bg-gray-50 p-2">
            <Checkbox
              label={`${periods.find((p) => p.position === item.periodPosition)?.name ?? t('curriculum.period', { period: item.periodPosition })} · ${item.title}`}
              description={item.logros.join(' · ')}
              checked={!excluded.has(item.key)}
              onChange={(event) =>
                setExcluded((current) => {
                  const next = new Set(current);
                  if (event.target.checked) next.delete(item.key);
                  else next.add(item.key);
                  return next;
                })
              }
            />
          </li>
        ))}
      </ul>
      <div className="flex justify-end">
        <Button
          onClick={() => seed.mutate()}
          loading={seed.isPending}
          disabled={excluded.size === items.length}
        >
          {t('curriculum.seed.confirm', { count: items.length - excluded.size })}
        </Button>
      </div>
    </div>
  );
};
